import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import pluckrLogo from '../assets/images/pluckr-logo.png';
import ThemeToggle from '@/components/ThemeToggle';
import { useThemeMode } from '@/hooks/useThemeMode';

export default function ResendVerification() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get('email') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { resolvedTheme } = useThemeMode();
  const isDark = resolvedTheme === 'dark';

  const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.pluckr.ai');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSuccess(true);
        toast.success('Verification email sent!');
      } else {
        toast.error(data.message || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const brandGreen = '#9be38f';

  return (
    <div className="min-h-screen w-full bg-background grid-pattern" data-testid="resend-verification-page">
      <Toaster
        position="top-right"
        theme={resolvedTheme}
        toastOptions={{
          style: {
            background: isDark ? '#282929' : '#ffffff',
            color: isDark ? '#ffffff' : '#1a1a1a',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e5e5e5',
          },
        }}
      />

      <ThemeToggle className="fixed top-4 right-4 z-[60]" />

      {/* Top-left brand */}
      <div className="px-8 pt-7">
        <Link
          to="/"
          className="inline-flex items-center gap-3 text-black/90 dark:text-white"
          style={{ fontFamily: "'Lora', Georgia, serif" }}
        >
          <img src={pluckrLogo} alt="Pluckr" className="h-9 w-auto" />
          <span className="text-[22px] font-bold tracking-[-0.01em]">Pluckr</span>
        </Link>
      </div>

      <div className="max-w-[1200px] mx-auto px-8 pb-16 pt-10">
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-14">
          {/* Left headline */}
          <div className="flex-1 w-full lg:pt-10">
            <h1
              className="text-[56px] sm:text-[72px] font-black leading-[0.95] tracking-[-0.03em]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="block text-black/80 dark:text-white">Resend</span>
              <span className="block" style={{ color: brandGreen }}>
                verification
              </span>
            </h1>
          </div>

          {/* Right card */}
          <div className="w-full max-w-[420px]">
            <div
              className="bg-white dark:bg-[#282929] rounded-[18px] shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-black/5 dark:border-white/15 p-8"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {!isSuccess ? (
                <>
                  <p className="text-[14px] text-black/60 dark:text-white/70 mb-6 leading-relaxed">
                    Enter your email address and we'll send you a new verification link.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[12px] font-medium text-black/70 dark:text-white/70">E-mail address</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full h-11 rounded-md border border-black/10 dark:border-white/20 bg-white dark:bg-[#282929] text-black dark:text-white px-3 text-[14px] outline-none focus:border-black/20 dark:focus:border-white/30"
                        placeholder=""
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-11 rounded-full font-semibold text-[13px] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#1f2d2a' }}
                    >
                      {isLoading ? 'Sending...' : 'Send Verification Email'}
                    </button>

                    <div className="pt-3 text-[12px] text-black/60 dark:text-white/70">
                      <div>
                        Remember your password?{' '}
                        <Link
                          to="/early-access"
                          className="font-semibold text-black/75 dark:text-white hover:text-black dark:hover:text-white"
                        >
                          Log in ›
                        </Link>
                      </div>
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center py-4">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: brandGreen }}
                  >
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1a1a1a"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 className="text-[20px] font-bold text-black/80 dark:text-white mb-3">
                    Check Your Email
                  </h3>
                  <p className="text-[14px] text-black/60 dark:text-white/70 mb-6 leading-relaxed">
                    If an unverified account exists with this email, you'll receive a new verification link shortly.
                  </p>
                  <Link
                    to="/early-access"
                    className="w-full h-11 rounded-full font-semibold text-[13px] text-white transition-colors flex items-center justify-center"
                    style={{ backgroundColor: '#1f2d2a' }}
                  >
                    Back to Login
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
